import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Modal } from "./Modal";
import type { Ingredient } from "../types";

interface MergeResult {
  mergedIngredients: number;
  recipesUpdated: number;
  fridgeMerged: boolean;
  shoppingListsUpdated: number;
}

export function IngredientMergeTool({
  open,
  ingredients,
  onClose,
  onSuccess,
}: {
  open: boolean;
  ingredients: Ingredient[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [targetId, setTargetId] = useState("");
  const [sourceIds, setSourceIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [result, setResult] = useState<MergeResult | null>(null);

  const sorted = useMemo(
    () => [...ingredients].sort((a, b) => a.name.localeCompare(b.name, "cs")),
    [ingredients]
  );

  const filteredSources = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return sorted.filter((i) => i.id !== targetId && (!f || i.name.toLowerCase().includes(f)));
  }, [sorted, targetId, filter]);

  const target = sorted.find((i) => i.id === targetId);
  const selectedSources = sorted.filter((i) => sourceIds.has(i.id));

  const unitWarnings = useMemo(() => {
    if (!target) return [];
    return selectedSources.filter((s) => s.unit !== target.unit).map((s) => s.name);
  }, [target, selectedSources]);

  const merge = useMutation({
    mutationFn: () =>
      api.post<MergeResult>("/api/ingredients/merge", {
        targetId,
        sourceIds: [...sourceIds],
      }),
    onSuccess: (res) => {
      setResult(res);
      onSuccess();
    },
  });

  const handleClose = () => {
    setTargetId("");
    setSourceIds(new Set());
    setFilter("");
    setResult(null);
    onClose();
  };

  const toggleSource = (id: string) => {
    setSourceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canMerge = !!targetId && sourceIds.size > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Sloučit suroviny"
      wide
      footer={
        result ? (
          <button className="btn-primary" onClick={handleClose}>
            Zavřít
          </button>
        ) : (
          <>
            <button className="btn-ghost" onClick={handleClose}>
              Zrušit
            </button>
            <button
              className="btn-primary flex items-center gap-1"
              disabled={!canMerge || merge.isPending}
              onClick={() => {
                const names = selectedSources.map((s) => `"${s.name}"`).join(", ");
                if (
                  !confirm(
                    `Sloučit ${names} do suroviny "${target?.name}"?\n\nDuplicitní záznamy se smažou, vazby v receptech se přepíší.`
                  )
                ) {
                  return;
                }
                merge.mutate();
              }}
            >
              <span aria-hidden>🔗</span>
              <span>{merge.isPending ? "Slučuji…" : "Sloučit"}</span>
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-2 text-sm">
          <p className="text-green-800 font-medium flex items-center gap-2">
            <span aria-hidden>✅</span>
            <span>Sloučení dokončeno.</span>
          </p>
          <ul className="list-disc pl-5 text-slate-700 space-y-0.5">
            <li>Sloučeno surovin: {result.mergedIngredients}</li>
            <li>Upraveno vazeb v receptech: {result.recipesUpdated}</li>
            {result.fridgeMerged && <li>Sloučeno zásob v lednici</li>}
            {result.shoppingListsUpdated > 0 && (
              <li>Upraveno položek nákupních seznamů: {result.shoppingListsUpdated}</li>
            )}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Vyberte surovinu, kterou chcete <strong>ponechat</strong>, a zaškrtněte duplicity ke sloučení
            (např. „Brambora“ → „Brambory“). Všechny vazby v receptech se přepíší na cílovou surovinu.
          </p>

          <div>
            <label className="label" htmlFor="merge-target">
              Ponechat surovinu
            </label>
            <select
              id="merge-target"
              className="input"
              value={targetId}
              onChange={(e) => {
                setTargetId(e.target.value);
                setSourceIds((prev) => {
                  const next = new Set(prev);
                  next.delete(e.target.value);
                  return next;
                });
              }}
            >
              <option value="">— vyberte cílovou surovinu —</option>
              {sorted.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.unit})
                </option>
              ))}
            </select>
          </div>

          {targetId && (
            <div>
              <label className="label" htmlFor="merge-filter">
                Sloučit do „{target?.name}“ tyto suroviny
              </label>
              <input
                id="merge-filter"
                className="input mb-2"
                placeholder="🔍 Filtrovat…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filteredSources.length === 0 ? (
                <p className="text-sm text-slate-500">Žádné další suroviny k výběru.</p>
              ) : (
                <div className="max-h-56 overflow-y-auto border rounded-lg divide-y">
                  {filteredSources.map((i) => (
                    <label
                      key={i.id}
                      className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={sourceIds.has(i.id)}
                        onChange={() => toggleSource(i.id)}
                      />
                      <span className="flex-1 font-medium">{i.name}</span>
                      <span className="text-slate-500">{i.unit}</span>
                      {i.packageSize === 0 ? (
                        <span className="text-[10px] uppercase tracking-wide rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">
                          Na váhu
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">
                          {i.packageSize} {i.unit}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}
              {sourceIds.size > 0 && (
                <p className="text-xs text-slate-500 mt-2">Vybráno ke sloučení: {sourceIds.size}</p>
              )}
            </div>
          )}

          {unitWarnings.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">Upozornění — rozdílné jednotky</p>
              <p className="mt-1">
                Suroviny {unitWarnings.join(", ")} mají jinou jednotku než cílová „{target?.name}“ ({target?.unit}).
                Množství v receptech se nesčítá automaticky — zkontrolujte recepty po sloučení.
              </p>
            </div>
          )}

          {merge.isError && (
            <p className="text-sm text-red-600">Sloučení se nezdařilo. Zkuste to znovu.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
